import { memo, useCallback } from "react";
import { Platform, Pressable } from "react-native";
import "@visioni/a3s-ui/theme";
import { StyleSheet } from "react-native-unistyles";
import { View, Text, SegmentedControl, Checkbox } from "@visioni/a3s-ui/components";
import { AccessCardThumbnail } from "./AccessCardThumbnail";

export type AccessCardProps = {
  id: string;
  name: string;
  subtitle?: string;
  thumbnailUrl?: string | null | undefined;
  /** Whether to show the thumbnail. Defaults to true. */
  showThumbnail?: boolean;
  /** Whether to show the role segmented control. Defaults to true. */
  showRoleControl?: boolean;
  /** Disables all interaction, dims the card. */
  disabled?: boolean;
  selected: boolean;
  role: "manager" | "viewer";
  onToggle: (id: string) => void;
  onRoleChange: (id: string, role: "manager" | "viewer") => void;
};

const ROLE_OPTIONS = [{ title: "Manager" }, { title: "Viewer" }] as const;
const WEB_SELECTOR_WIDTH = 180;

export const AccessCard = memo(
  ({
    id,
    name,
    subtitle,
    thumbnailUrl,
    showThumbnail = true,
    showRoleControl = true,
    disabled = false,
    selected,
    role,
    onToggle,
    onRoleChange,
  }: AccessCardProps) => {
    const handlePress = useCallback(() => {
      if (disabled) return;
      onToggle(id);
    }, [onToggle, id, disabled]);

    const handleCheckboxChange = useCallback(() => {
      if (disabled) return;
      onToggle(id);
    }, [onToggle, id, disabled]);

    const handleRoleChange = useCallback(
      (option: string) => {
        if (disabled) return;
        const newRole = option.toLowerCase() as "manager" | "viewer";
        if (!selected) {
          onToggle(id);
          onRoleChange(id, newRole);
          return;
        }
        if (newRole !== role) onRoleChange(id, newRole);
      },
      [onRoleChange, onToggle, id, role, selected, disabled],
    );

    const selectedOption = role === "manager" ? "Manager" : "Viewer";

    return (
      <Pressable
        onPress={handlePress}
        accessibilityRole="checkbox"
        accessibilityState={{ checked: selected }}
        disabled={disabled}
      >
        <View
          bg="surfaceCard"
          flexDirection="row"
          alignItems="center"
          style={[styles.card, selected && styles.cardSelected, disabled && styles.cardDisabled]}
        >
          {showThumbnail && <AccessCardThumbnail url={thumbnailUrl} />}

          <View style={styles.nameContainer}>
            <Text textStyle="sm/medium" numberOfLines={2} {...(disabled ? { color: "textMuted" as const } : {})}>
              {name}
            </Text>
            {subtitle && (
              <Text textStyle="xxs/regular" color="textMuted" numberOfLines={1}>
                {subtitle}
              </Text>
            )}
          </View>

          {showRoleControl && (
            <View style={styles.controlContainer}>
              <SegmentedControl
                options={ROLE_OPTIONS}
                selectedOption={selectedOption}
                onSelectOption={handleRoleChange}
                containerStyle={styles.segmentedContainer}
                isSelectionVisible={selected}
                disabled={disabled}
                {...(Platform.OS === "web" ? { width: WEB_SELECTOR_WIDTH } : {})}
              />
            </View>
          )}

          <Checkbox checked={selected} size={20} onChange={handleCheckboxChange} disabled={disabled} />
        </View>
      </Pressable>
    );
  },
);

const styles = StyleSheet.create((theme) => ({
  card: {
    borderRadius: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    padding: theme.spacing.xl,
    gap: theme.spacing.lg,
    flexWrap: "wrap",
    userSelect: "none",
  },
  cardSelected: {
    borderColor: theme.colors.interactive.controls.focused_border_outline,
  },
  cardDisabled: {
    opacity: 0.5,
  },
  nameContainer: {
    flex: 1,
    minWidth: 100,
    gap: 2,
  },
  controlContainer: {
    flexShrink: 0,
  },
  segmentedContainer: {
    backgroundColor: "transparent",
  },
}));
